import { Injectable, inject, signal } from '@angular/core';
import { Store, Actions, ofActionDispatched, ofActionCompleted } from '@ngxs/store';
import { firstValueFrom, Subscription, BehaviorSubject, combineLatest, filter, take, timeout } from 'rxjs';
import { VCPM } from 'pcs-domain';
import { ProjectCollaborationService } from './project-collaboration.service';
import { VCPMService } from './vcpm.service';
import { PCSSizerSerive } from './pcsizer.service';
import { AlertService } from './alert/alert.service';
import { AuthState } from '../../store/auth/auth.state';
import { ProjectDetailsState } from '../../store/project-details/project-details.state';
import { RefreshProjectCollaboration, UpdateProject, UpdateProjectMetadata, SaveProject, DeleteWorkloadDomain } from '../../store/project-details/project-details.actions';

type SessionStatus = 'loading' | 'acquiring' | 'editing' | 'read-only' | 'validating' | 'releasing' | 'release-failed';
interface PendingRelease { projectId: string; userEmail: string; }
interface TabLease { tabId: string; expires: number; }

/** Owns only collaboration state. Server responses never replace sizing inputs. */
@Injectable({ providedIn: 'root' })
export class ProjectEditSessionService {
  private store = inject(Store);
  private actions = inject(Actions);
  private saveSubscriptions = new Subscription();
  private activeSaves = new Set<object>();
  private saveCount = new BehaviorSubject(0);
  private saveFailures = 0;
  private api = inject(VCPMService);
  private collaboration = inject(ProjectCollaborationService);
  private sizer = inject(PCSSizerSerive);
  private alerts = inject(AlertService);
  readonly status = signal<SessionStatus>('loading');
  readonly message = signal('');
  readonly saveWaitFailed = signal(false);
  readonly pending = signal<PendingRelease[]>([]);
  private readonly leaseRenewalMs = 30_000;
  private readonly requestMs = 15_000;
  private readonly tabId = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  private projectId: string | null = null;
  private acquired = false;
  private acquisitionUncertain = false;
  private released = false;
  private serverLockReleased = false;
  private exitNavigationId?: number;
  private cancelledExit = false;
  private timer?: ReturnType<typeof setInterval>;
  private validating?: Promise<boolean>;
  private releaseRequest?: Promise<boolean>;
  private acquisition?: Promise<void>;
  private retryRequest?: Promise<void>;
  private accessSub?: Subscription;
  private releaseBrowserLock?: () => void;
  private childSaving = false;
  private suspended = false;
  private generation = 0;
  private stateSub?: Subscription;
  private readonly storage = (event: StorageEvent) => {
    if (!this.suspended && event.key === this.leaseKey() && this.projectId && this.otherTabActive(this.projectId)) {
      this.acquired = false;
      this.setStatus('read-only', 'This project is being edited in another browser tab.');
    }
  };
  private readonly pagehide = () => this.suspendDocument();
  private readonly pageshow = (event: PageTransitionEvent) => {
    if (!event.persisted || !this.suspended || !this.projectId) return;
    this.suspended = false;
    this.startLeaseRenewal();
    void this.retry();
  };

  begin(projectId: string): void {
    this.end();
    this.projectId = projectId;
    this.acquired = false;
    this.acquisitionUncertain = false;
    this.released = false;
    this.serverLockReleased = false;
    this.exitNavigationId = undefined;
    this.cancelledExit = false;
    this.saveWaitFailed.set(false);
    this.suspended = false;
    this.setStatus('loading');
    this.saveSubscriptions = new Subscription();
    this.saveSubscriptions.add(this.actions.pipe(ofActionDispatched(UpdateProject, UpdateProjectMetadata, SaveProject, DeleteWorkloadDomain), filter(action => this.isCurrentProjectSave(action))).subscribe(action => {
      this.activeSaves.add(action);
      this.saveCount.next(this.activeSaves.size);
      this.updateReleaseMessage();
    }));
    this.saveSubscriptions.add(this.actions.pipe(ofActionCompleted(UpdateProject, UpdateProjectMetadata, SaveProject, DeleteWorkloadDomain)).subscribe(({ action, result }) => {
      if (this.activeSaves.has(action) && !result.successful) this.saveFailures++;
      this.activeSaves.delete(action);
      this.saveCount.next(this.activeSaves.size);
      this.updateReleaseMessage();
    }));
    this.saveSubscriptions.add(this.sizer.childSaveInProgressFor$(projectId).subscribe(saving => {
      this.childSaving = saving;
      this.updateReleaseMessage();
    }));
    this.stateSub = this.store.select(ProjectDetailsState.getCurrentProject).subscribe(project => {
      if (project && String(project.projectId) === this.projectId) this.observe(project);
    });
    this.accessSub = this.collaboration.editAccessLost$.subscribe(projectId => {
      if (projectId !== this.projectId) return;
      this.acquired = false;
      if (!this.acquisitionUncertain) this.dropLease();
      if (this.status() !== 'releasing') {
        this.setStatus('read-only', 'Your lock or edit access has changed. Retry edit access to continue.');
      }
    });
    window.addEventListener('storage', this.storage);
    window.addEventListener('pagehide', this.pagehide);
    window.addEventListener('pageshow', this.pageshow);
    this.startLeaseRenewal();
  }

  private isCurrentProjectSave(action: UpdateProject | UpdateProjectMetadata | SaveProject | DeleteWorkloadDomain): boolean {
    if (action instanceof UpdateProject) return String(action.projectId) === this.projectId;
    if (action instanceof UpdateProjectMetadata) return String(action.project.projectId) === this.projectId;
    if (action instanceof SaveProject) return String(action.projectData?.projectId) === this.projectId;
    // DeleteWorkloadDomain persists the currently loaded project directly.
    return !!this.current();
  }

  private startLeaseRenewal(): void {
    if (this.timer) clearInterval(this.timer);
    // Maintain only the local tab lease. Lock status is refreshed on user request, never polled.
    this.timer = setInterval(() => {
      if ((this.acquired || this.acquisitionUncertain) && this.projectId) this.renewLease();
    }, this.leaseRenewalMs);
  }

  private observe(project: VCPM.Project): void {
    if (this.suspended || this.released || this.status() === 'releasing' || this.status() === 'acquiring' || this.status() === 'validating') return;
    if (!this.collaboration.canEditProject(project) || this.collaboration.isAnalysisLocked(project)) {
      this.acquired = false;
      this.dropLease();
      this.setStatus('read-only', this.collaboration.isAnalysisLocked(project) ? '' : 'This project is read-only or your edit access has changed.');
    } else if (!this.collaboration.isAnalysisNeedLock(project) && !project.locked) {
      this.setStatus('editing');
    } else if (this.acquired && project.locked && this.collaboration.isCurrentUserEmail(project.lockedBy)) {
      if (this.status() !== 'release-failed') this.setStatus('editing');
    } else if (this.status() === 'loading') {
      this.acquisition = this.acquire(project);
    }
  }

  retry(): Promise<void> {
    if (this.retryRequest) return this.retryRequest;
    const project = this.current();
    if (this.suspended || !project || ['loading', 'acquiring', 'validating', 'releasing'].includes(this.status())) return Promise.resolve();
    const generation = this.generation;
    this.setStatus('validating', this.message());
    const request = (async () => {
      try {
        const fresh = await firstValueFrom(this.api.fetchProjectDataById(String(project.projectId)).pipe(timeout(this.requestMs)));
        if (generation !== this.generation) return;
        // Exit owns the session now: update its snapshot without acquiring a new lock.
        if (this.status() === 'releasing') { this.merge(fresh); return; }
        if (!this.collaboration.canEditProject(fresh) || this.collaboration.isAnalysisLocked(fresh)) {
          this.setStatus('read-only', this.collaboration.isAnalysisLocked(fresh) ? '' : 'This project is read-only.');
          this.merge(fresh);
          return;
        }
        this.released = false;
        if (!fresh.locked && !this.collaboration.isAnalysisNeedLock(fresh)) {
          this.setStatus('editing');
          this.merge(fresh);
          return;
        }
        this.merge(fresh);
        this.acquisition = this.acquire(fresh);
        await this.acquisition;
      } catch {
        if (generation === this.generation && this.status() !== 'releasing') {
          this.setStatus('read-only', 'Could not verify edit access. Please retry.');
        }
      } finally {
        if (generation === this.generation) this.retryRequest = undefined;
      }
    })();
    this.retryRequest = request;
    return request;
  }

  private async acquire(project: VCPM.Project): Promise<void> {
    if (!this.projectId || !this.collaboration.canEditProject(project)) return;
    const generation = this.generation;
    const projectId = this.projectId;
    this.setStatus('acquiring');
    if (!await this.claimLease()) {
      if (generation !== this.generation) return;
      this.setStatus('read-only', 'This project is being edited in another browser tab.');
      return;
    }
    if (generation !== this.generation) return;
    try {
      // A failed response cannot prove that the server did not acquire the lock.
      this.acquisitionUncertain = true;
      this.remember(project);
      const response = await firstValueFrom(this.api.lockUnlockProject(projectId, 'lock').pipe(timeout(this.requestMs)));
      if (generation !== this.generation) return;
      if (!response.locked || !this.collaboration.isCurrentUserEmail(response.lockedBy) || !this.collaboration.canEditProject(response)) throw new Error('Lock acquisition was not confirmed.');
      this.acquired = true;
      this.acquisitionUncertain = false;
      this.forget(projectId);
      this.setStatus('editing');
      this.merge(response);
    } catch {
      if (generation !== this.generation) return;
      this.acquired = false;
      if (!this.acquisitionUncertain) this.dropLease();
      this.setStatus('read-only', 'Could not acquire the project lock. Refresh status and retry.');
    }
  }

  private current(): VCPM.Project | undefined {
    const project = this.store.selectSnapshot(ProjectDetailsState.getCurrentProject);
    return project && String(project.projectId) === this.projectId ? project : undefined;
  }

  private merge(project: VCPM.Project): void {
    this.store.dispatch(new RefreshProjectCollaboration(project));
  }

  private setStatus(status: SessionStatus, message = ''): void {
    this.status.set(status);
    this.message.set(status === 'releasing' ? this.releaseMessage() : message);
    this.collaboration.setEditorBlocked(this.projectId, status !== 'editing');
  }

  private releaseMessage(): string {
    if (this.childSaving || this.activeSaves.size > 0) return 'Finishing save…';
    const project = this.current();
    return this.acquisitionUncertain || (project?.locked && this.collaboration.isCurrentUserEmail(project.lockedBy)) ? 'Unlocking…' : '';
  }

  private updateReleaseMessage(): void {
    if (this.status() === 'releasing') this.message.set(this.releaseMessage());
  }

  validate(blockWrites = true): Promise<boolean> {
    if (this.validating) return this.validating;
    const projectId = this.projectId;
    if (this.suspended || !projectId || this.released || this.status() === 'acquiring' || this.status() === 'releasing') return Promise.resolve(false);
    const generation = this.generation;
    if (blockWrites) this.setStatus('validating');
    const request = (async () => {
      try {
        const project = await firstValueFrom(this.api.fetchProjectDataById(projectId).pipe(timeout(this.requestMs))) as VCPM.Project;
        if (generation !== this.generation) return false;
        const allowed = !this.otherTabActive(projectId) && this.collaboration.canEditProject(project) && !this.collaboration.isAnalysisLocked(project) &&
          (!this.collaboration.isAnalysisNeedLock(project) || (this.acquired && project.locked && this.collaboration.isCurrentUserEmail(project.lockedBy)));
        if (this.status() !== 'releasing') this.setStatus(allowed ? 'editing' : 'read-only', allowed || this.collaboration.isAnalysisLocked(project) ? '' : 'Your lock or edit access has changed.');
        if (!allowed) { this.acquired = false; this.dropLease(); }
        this.merge(project);
        this.validating = undefined;
        return allowed;
      } catch {
        if (generation === this.generation) {
          this.validating = undefined;
          if (this.status() !== 'releasing') this.setStatus('read-only', 'Could not verify edit access. Refresh status and retry.');
        }
        return false;
      }
    })();
    this.validating = request;
    void request.finally(() => { if (this.validating === request) this.validating = undefined; });
    return request;
  }

  /** Called only after the router's unsaved-changes decision. */
  release(navigationId?: number): Promise<boolean> {
    this.exitNavigationId = navigationId;
    this.cancelledExit = false;
    if (this.releaseRequest) return this.releaseRequest;
    if (this.released) return Promise.resolve(true);
    this.serverLockReleased = false;
    const generation = this.generation;
    const project = this.current();
    const previousStatus = this.status();
    const saveFailures = this.saveFailures;
    let phase: 'saving' | 'unlocking' = 'saving';
    this.saveWaitFailed.set(false);
    const request = (async () => {
      if (!project) return true;
      if (this.suspended) return false;
      if (!navigator.locks && this.otherTabActive(String(project.projectId))) return true;
      this.setStatus('releasing');
      try {
        if (this.retryRequest) await this.retryRequest;
        if (generation !== this.generation) return false;
        if (this.acquisition) await this.acquisition;
        if (generation !== this.generation) return false;
        this.setStatus('releasing');
        await firstValueFrom(combineLatest([this.sizer.childSaveInProgressFor$(String(project.projectId)), this.saveCount]).pipe(
          filter(([childSaving, count]) => !childSaving && count === 0), take(1), timeout(this.requestMs)));
        if (this.saveFailures !== saveFailures) throw new Error('Save failed while leaving.');
        if (this.validating) await this.validating;
        if (generation !== this.generation) return false;
        this.setStatus('releasing');
        phase = 'unlocking';
        const latest = this.current();
        if (latest && (this.acquisitionUncertain || (latest.locked && this.collaboration.isCurrentUserEmail(latest.lockedBy)))) {
          const reconcile = async (): Promise<boolean> => {
            this.remember(latest);
            const fresh = await firstValueFrom(this.api.fetchProjectDataById(String(latest.projectId)).pipe(timeout(this.requestMs)));
            if (generation !== this.generation) return false;
            if (fresh.locked && this.collaboration.isCurrentUserEmail(fresh.lockedBy)) {
              const response = await firstValueFrom(this.api.lockUnlockProject(String(latest.projectId), 'unlock').pipe(timeout(this.requestMs)));
              if (response.locked) throw new Error('Release was not confirmed.');
              if (generation !== this.generation) return false;
              this.serverLockReleased = true;
              this.merge(response);
              this.collaboration.notifyCollaborators(latest).subscribe({ error: () => {} });
            }
            this.forget(String(latest.projectId));
            return true;
          };
          if (navigator.locks && !this.releaseBrowserLock) {
            // A read-only tab must not release another editor's lock on navigation either.
            const completed = await navigator.locks.request(this.leaseKey(String(latest.projectId)), { ifAvailable: true }, lock =>
              lock ? reconcile() : true);
            if (!completed) return false;
          } else if (!await reconcile()) return false;
        }
        if (generation === this.generation) {
          this.released = true;
          this.acquired = false;
          this.acquisitionUncertain = false;
          this.dropLease();
          this.releaseRequest = undefined;
          if (this.cancelledExit) this.restoreAfterCancelledExit();
        }
        return true;
      } catch {
        if (generation === this.generation) {
          this.releaseRequest = undefined;
          if (phase === 'saving') {
            this.saveWaitFailed.set(true);
            const current = this.current();
            const canResume = previousStatus === 'editing' && !!current && this.collaboration.canEditProject(current) &&
              !this.collaboration.isAnalysisLocked(current) &&
              (!this.collaboration.isAnalysisNeedLock(current) || this.acquired);
            this.setStatus(canResume ? 'editing' : 'read-only',
              this.saveFailures !== saveFailures
                ? 'Changes were not saved. Your project is still open; please retry saving.'
                : 'Saving has not finished. Your project is still open; wait for the save or retry.');
          } else {
            this.setStatus('release-failed', 'Could not release the lock. Your project is still open; please retry.');
          }
        }
        return false;
      }
    })();
    this.releaseRequest = request;
    void request.finally(() => { if (this.releaseRequest === request) this.releaseRequest = undefined; });
    return request;
  }

  navigationCancelled(navigationId?: number): void {
    if (navigationId !== undefined && navigationId !== this.exitNavigationId) return;
    this.cancelledExit = true;
    if (this.projectId && this.released) this.restoreAfterCancelledExit();
  }

  private restoreAfterCancelledExit(): void {
    if (this.serverLockReleased) {
      this.setStatus('read-only', 'Navigation was cancelled after release. Retry edit access to continue working.');
    } else {
      this.released = false;
      this.setStatus('loading');
      const project = this.current();
      if (project) this.observe(project);
    }
  }

  async beforeLogout(): Promise<void> {
    await this.release();
    this.end();
  }

  loadPending(): void { this.pending.set(this.readPending()); }
  private readPending(): PendingRelease[] {
    try {
      const value = JSON.parse(localStorage.getItem('pcs-pending-unlocks') ?? '[]');
      return Array.isArray(value) ? value.filter(p => typeof p?.projectId === 'string' && typeof p?.userEmail === 'string') : [];
    } catch { return []; }
  }
  private writePending(entries: PendingRelease[]): void {
    this.pending.set(entries);
    try { localStorage.setItem('pcs-pending-unlocks', JSON.stringify(entries)); } catch { /* Storage may be unavailable. */ }
  }
  private remember(project: VCPM.Project): void {
    const userEmail = this.store.selectSnapshot(AuthState.getLoggedInUserProfile)?.acct ?? '';
    const entries = this.readPending();
    if (!entries.some(p => p.projectId === String(project.projectId) && p.userEmail === userEmail)) {
      entries.push({ projectId: String(project.projectId), userEmail });
    }
    this.writePending(entries);
  }
  private forget(projectId: string): void {
    const userEmail = this.store.selectSnapshot(AuthState.getLoggedInUserProfile)?.acct ?? '';
    this.writePending(this.readPending().filter(p => p.projectId !== projectId || p.userEmail !== userEmail));
  }

  /** Explicit recovery: the API cannot distinguish an old same-user session on another device. */
  async recover(project: VCPM.Project): Promise<boolean> {
    const projectId = String(project.projectId);
    const refuse = () => {
      this.alerts.showAlert('warning', 'Close the editing tab before releasing its lock.', true, 5000);
      return false;
    };
    if (this.projectId === projectId || (!navigator.locks && this.otherTabActive(projectId))) return refuse();
    const recoverUnderLease = async (ownsMutex = false): Promise<boolean> => {
      if (this.projectId === projectId || (!ownsMutex && this.otherTabActive(projectId))) return refuse();
      this.renewLease(projectId, ownsMutex);
      try {
        const fresh = await firstValueFrom(this.api.fetchProjectDataById(projectId).pipe(timeout(this.requestMs)));
        if (!fresh.locked || !this.collaboration.isCurrentUserEmail(fresh.lockedBy)) { this.forget(projectId); return true; }
        if (this.projectId === projectId || (!ownsMutex && this.otherTabActive(projectId))) return refuse();
        this.remember(fresh);
        const response = await firstValueFrom(this.api.lockUnlockProject(projectId, 'unlock').pipe(timeout(this.requestMs)));
        if (response.locked) throw new Error('Release was not confirmed.');
        this.forget(projectId);
        return true;
      } finally {
        this.removeStoredLease(projectId);
      }
    };
    try {
      if (navigator.locks) {
        return await navigator.locks.request(this.leaseKey(projectId), { ifAvailable: true }, lock =>
          lock ? recoverUnderLease(true) : refuse());
      }
      // Browsers without Web Locks retain the existing storage-based coordination.
      return await recoverUnderLease();
    } catch {
      this.alerts.showAlert('danger', 'Could not release the project lock. Please retry.', true, 5000);
      return false;
    }
  }

  private leaseKey(projectId = this.projectId): string { return `pcs-editor-${projectId}`; }
  otherTabActive(projectId: string): boolean {
    if (this.releaseBrowserLock && projectId === this.projectId) return false;
    try {
      const lease: TabLease = JSON.parse(localStorage.getItem(this.leaseKey(projectId)) ?? 'null');
      return !!lease && lease.tabId !== this.tabId && lease.expires > Date.now();
    } catch { return false; }
  }
  private async claimLease(): Promise<boolean> {
    if (!this.projectId || this.suspended) return false;
    if (!navigator.locks && this.otherTabActive(this.projectId)) return false;
    if (navigator.locks && !this.releaseBrowserLock) {
      const generation = this.generation;
      const granted = await new Promise<boolean>(resolve => {
        void navigator.locks.request(this.leaseKey(), { ifAvailable: true }, async lock => {
          if (!lock || generation !== this.generation) { resolve(false); return; }
          await new Promise<void>(release => { this.releaseBrowserLock = release; resolve(true); });
        }).catch(() => resolve(false));
      });
      if (!granted) return false;
    }
    this.renewLease();
    return !this.otherTabActive(this.projectId);
  }
  private renewLease(projectId = this.projectId, ownsMutex = false): void {
    if (!projectId || (!ownsMutex && this.otherTabActive(projectId))) return;
    try { localStorage.setItem(this.leaseKey(projectId), JSON.stringify({ tabId: this.tabId, expires: Date.now() + 120_000 })); } catch { /* Best effort. */ }
  }
  private dropLease(): void {
    this.removeStoredLease();
    this.releaseBrowserLock?.();
    this.releaseBrowserLock = undefined;
  }
  private removeStoredLease(projectId = this.projectId): void {
    try {
      const lease = JSON.parse(localStorage.getItem(this.leaseKey(projectId)) ?? 'null');
      if (lease?.tabId === this.tabId) localStorage.removeItem(this.leaseKey(projectId));
    } catch { /* Best effort. */ }
  }
  private suspendDocument(): void {
    if (this.suspended || !this.projectId) return;
    const project = this.current();
    if (project && (this.acquisitionUncertain ||
      (this.acquired && project.locked && this.collaboration.isCurrentUserEmail(project.lockedBy)))) this.remember(project);
    // Unload cannot distinguish closing from reloading. Never send an unlock
    // that could arrive after a new document reacquires this user's server lock.
    this.suspended = true;
    this.generation++;
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    this.acquired = false;
    this.acquisitionUncertain = false;
    this.acquisition = undefined;
    this.retryRequest = undefined;
    this.validating = undefined;
    this.releaseRequest = undefined;
    this.dropLease();
    this.setStatus('read-only');
  }

  end(): void {
    this.generation++;
    this.stateSub?.unsubscribe();
    this.accessSub?.unsubscribe();
    this.saveSubscriptions.unsubscribe();
    this.activeSaves.clear();
    this.saveFailures = 0;
    this.saveCount.next(0);
    if (this.timer) clearInterval(this.timer);
    window.removeEventListener('storage', this.storage);
    window.removeEventListener('pagehide', this.pagehide);
    window.removeEventListener('pageshow', this.pageshow);
    this.dropLease();
    this.collaboration.setEditorBlocked(null, false);
    this.projectId = null;
    this.validating = undefined;
    this.releaseRequest = undefined;
    this.acquisition = undefined;
    this.retryRequest = undefined;
    this.acquisitionUncertain = false;
    this.suspended = false;
    this.childSaving = false;
  }
}
